import {MsgOver, OSkinableInit} from "back/commons/basis";
import {IReg, IRegPointer, IUiEnv, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {GridColDef, IGridLineDrawer} from "back/commons/widgets/grid-core";
import {ICoreUniverseEnv} from "lib/core/universe";
import {CellBuilderAccount} from "back/core/widgets/usersGrid";
import {JUser, JUserRoles} from "lib/core/user";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {AccelKeyMgr, Action, ACTION, IAction} from "lib/commons/actions";
import {GridDataRowJson} from "back/commons/widgets/grid-libs";
import {BaseAreaViewAsync, VIEWS} from "lib/commons/views";
import {POPUP} from "back/commons/widgets/popups";
import {AREAS, ControlAsyncArea, IArea, IControlAreaContext, IControlLabelContext} from "lib/commons/areas";
import {UserAccountInputArea, UserRolesInputArea} from "back/core/dialogs/userAreas";
import {FORMS} from "lib/commons/forms";
import {ERROR} from "lib/core/errorReport";
import "back/core/dialogs/dialogs_Perms";
import {GridDataHolderJsonTree} from "back/commons/widgets/tree";
import {IRoleEditUiHandlerContext, JUserRolesMap, JUserSpecifiedRolesMap, ORolesSelectorInit, RolesSelectorInput} from "lib/commons/roles";
import {GridSmall, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {IContextMenuActionsPointer} from "back/commons/actionables";
import {CellBuilderUserRoles} from "back/core/dialogs/usersMgr";
import {InputUserPanel, OInputUserInit} from "back/core/widgets/inputs";


export interface ObjRolesMapEdit<OBJCTX, E extends ICoreUniverseEnv & IUiEnv> extends BaseAreaViewAsync<E> {
	initialize(init: OObjRolesMapInit<OBJCTX, E>): this
}


/** Gestionnaire des rôles des users sur un objet métier : wsp, space, ... */
export interface OObjRolesMapInit<OBJCTX, E extends ICoreUniverseEnv & IUiEnv> extends OSkinableInit {
	reg: IReg<E>,
	/** Init de la liste des {user/rôles}. */
	grid?: OGridSmallInit,
	uiContext?: "popup" | undefined,
	/** Signature du niveau courant pour l'affectation des rôles
	 * {@link IRoleEditUiHandlerContext.uiLayerSgn}
	 */
	rolesUiLayerSng?: string,

	/** Contexte de l'objet courant */
	objThis: OBJCTX,

	/** Fonction retournant la liste des OUserRolesMap définis sur l'objet courant */
	objFetchRolesMap: (objThis: OBJCTX) => (Promise<JUserRolesMap>),

	/** Fonction retournant la liste des OUserRolesMap résolus d'une lise de users sur l'objet courrant */
	objFetchRolesMapForAccounts?: (objThis: OBJCTX, pAccounts: string[]) => Promise<JUserRolesMap>,

	/**
	 * Fonction permettant d'enregistrer la liste *complète* des OUserRolesMap de l'objet courant.
	 * Si la promesse est rejetée un message d'erreur sera affiché à l'utilisateur.
	 * Si null est retourné, on considère que l'action a échoué mais que l'utilisateur a déja été informé.
	 */
	objEditRolesMap: (objThis: OBJCTX, rolesMap: JUserSpecifiedRolesMap) => Promise<JUserRolesMap | null>,

	/** Role contrôlé sur le reg pour l'édition */
	objEditRolesMapPerms?: string[],

	/** Affiche l'entré "Utilisateurs non connectés" sur l'account spécifié */
	addAnonymousAccount?: string,

	/** accounts sélectionnées au load */
	preSelectAccounts?: string[],
}

/** Contexte des actions associées à un / plusieurs rolesMap */
export interface JRolesMapActionCtx extends IRegPointer<ICoreUniverseEnv & IUiEnv> {
	userRoles: JUserSetRoles[],
}

/** Roles du user account sur l'objet courant */
interface JUserSetRoles extends JUserRoles {
	account: string,
	/** Flag indiquant que cet account n'existe pas dans la base.*/
	userUnknown?: boolean,
}

export class ObjRolesMapEdit<OBJCTX, E extends ICoreUniverseEnv & IUiEnv> extends BaseAreaViewAsync<E> implements IGridLineDrawer, JRolesMapActionCtx, IControlLabelContext {
	reg: IReg<E>;

	/** Contexte d'initialisation */
	params: OObjRolesMapInit<OBJCTX, E>;

	// JRolesMapActionCtx
	/** userRoles actuellement sélectionnée */
	userRoles: JUserSetRoles[] = null;

	// IControlLabelContext
	buildControlLabel = true;

	formEditElt: HTMLFormElement;

	protected _rolesGridElt: GridSmall & IContextMenuActionsPointer<JRolesMapActionCtx>;
	protected _dataHolder: GridDataHolderJsonTree<JUserSetRoles>;
	protected _detailsBoxElt: HTMLDivElement;
	protected _headBoxElt: HTMLDivElement;
	protected _editUserRolesToolbar: BarActions<ObjRolesMapEdit<OBJCTX, E>>;
	protected _showToolbarTop: BarActions<JRolesMapActionCtx>;
	protected _showToolbarBottom: BarActions<JRolesMapActionCtx>;
	protected _formEditEmptyData: Dict<any> = {};
	protected _detailUserCurrentData: Jsonisable;

	protected async _initialize(init: OObjRolesMapInit<OBJCTX, E>): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.params = init;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("form-control-areas", sr);
		this._initAndInstallSkin("rolesmap-edit", init);

		// Accelkey
		let accelKeyMgr = new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:rolesmap", "accelkeys:rolesmap:" + this.id));
		accelKeyMgr.initFromMapActions(this.reg.mergeListsAsMap("accelkeys:rolesmap", "accelkeys:rolesmap:" + this.id));

		// Actions
		this.reg.addToList("actions:rolesmap:show:toolbar:top", "actionRefreshUi", 1, actionRefreshUi);
		this.reg.addToList("actions:rolesmap:show:toolbar:top", "actionNew", 1, new CreateEntryAction().requireVisiblePerm(this.params.objEditRolesMapPerms));
		this.reg.addToList("actions:rolesmap:edit:toolbar", 'formSave', 1, new UserFormSaveAction().requireVisiblePerm(this.params.objEditRolesMapPerms));
		this.reg.addToList("actions:rolesmap:edit:toolbar", 'formRevert', 1, new UserFormRevertAction().requireVisiblePerm(this.params.objEditRolesMapPerms));
		this.reg.addToList("actions:rolesmap:edit:toolbar", "deleteEntry", 1, new DeleteEntriesAction().requireVisiblePerm(this.params.objEditRolesMapPerms));

		// ContextMenu
		this.reg.addToList("actions:rolesmap:ctxtmenu", "actionDeleteEntry", 1, new DeleteEntriesAction().requireVisiblePerm(this.params.objEditRolesMapPerms));

		// Areas
		this.reg.addToList("areas:rolesmap:single", 'account', 1, new UserAccountInputArea().setReadOnly(true));
		this.reg.addToList("areas:rolesmap:new", 'account', 1, new SelectUserInputArea(null, this));
		this.reg.addToList("areas:rolesmap:single", 'grantedRoles', 1, new UserRolesInputArea(null, {uiLayerSgn: this.params.rolesUiLayerSng} as ORolesSelectorInit & IRoleEditUiHandlerContext));
		this.reg.addToList("areas:rolesmap:multi", 'grantedRoles', 1, new UserRolesInputArea(null, {uiLayerSgn: this.params.rolesUiLayerSng, canBeIndeterminate: true} as ORolesSelectorInit & IRoleEditUiHandlerContext));
		this.reg.addToList("areas:rolesmap:new", 'grantedRoles', 1, new UserRolesInputArea(null, {uiLayerSgn: this.params.rolesUiLayerSng} as ORolesSelectorInit & IRoleEditUiHandlerContext));

		this._headBoxElt = sr.appendChild(<div id="head" c-resizable/>) as HTMLDivElement;
		sr.appendChild(<c-resizer/>);
		this._detailsBoxElt = sr.appendChild(<div id="details" c-resizable hidden/>) as HTMLDivElement;

		// [head] Grid
		this._dataHolder = new GridDataHolderJsonTree<JUserSetRoles>();
		const columnDefs = [new GridColDef('tree')
			.setFlex('1rem', 1, 1).setMinWidth('55px').setLabel("Compte")
			.setCellBuilder(new CellBuilderAccount(this.reg, 'account', false, this.params.addAnonymousAccount ? [{account: this.params.addAnonymousAccount, isAnonymous: true}] : null)),
			new GridColDef('roles')
				.setLabel("Rôles").setDescription("Rôles résolus de l'utilisateur dans ce contexte").setFlex('5rem', 1, 1)
				.setCellBuilder(new CellBuilderUserRoles(this.reg, "roles"))
		];

		this._rolesGridElt = this._headBoxElt.appendChild(new GridSmall().initialize(Object.assign({
			reg: this.reg,
			selType: "multi",
			hideHeaders: false,
			columnDefs,
			emptyBody: () => this._lastLoadErrorStatus ? <span class="error">Accès aux rôles affectés en échec</span> : <span>Aucun rôle affecté à ce niveau</span>,
			skinOver: "rolesmap-edit-tree",
			lineDrawer: this,
			dataHolder: this._dataHolder,
		} as OGridSmallInit, init.grid)));

		this._rolesGridElt.ctxMenuActions = {
			actions: ACTION.injectSepByGroup(this.reg.mergeLists("actions:rolesmap", "actions:rolesmap:ctxtmenu", "actions:rolesmap:ctxtmenu:" + this.id) as IAction<JRolesMapActionCtx>[], this.reg.getPref("actions.rolesmap.groupOrder", "refresh users *"), this),
			actionContext: this,
		};

		this._rolesGridElt.addEventListener('keydown', async (ev: KeyboardEvent) => {
			return accelKeyMgr.handleKeyboardEvent(ev, this);
		});
		this._rolesGridElt.addEventListener('grid-select', async (ev: Event) => {
			this.userRoles = (this._dataHolder && this._dataHolder.countRows() > 0) ? this._dataHolder.getSelectedDatas() as JUserSetRoles[] : [];
			this._showToolbarTop.refreshContent();
			this._showToolbarBottom.refreshContent();
			await this.showDetails(this.userRoles);
		});

		// Toolbars
		let toolbarsBox = this._headBoxElt.appendChild(<div id="toolbarsBox"/>);
		this._showToolbarTop = toolbarsBox.appendChild(new BarActions<JRolesMapActionCtx>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:rolesmap", "actions:rolesmap:show:toolbar:top", "actions:rolesmap:show:toolbar:top:" + this.id, "actions:rolesmap:" + this.id),
				this.reg.getPref("actions.rolesmap.groupOrder", "refresh users *"), this),
			uiContext: "bar",
			actionContext: this,
			disableFullOverlay: true,
		} as OBarActionsInit<JRolesMapActionCtx>));
		this._showToolbarTop.setAttribute("class", "showToolbarTop");

		this._showToolbarBottom = toolbarsBox.appendChild(new BarActions<JRolesMapActionCtx>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:rolesmap:show:toolbar:bottom", "actions:rolesmap:show:toolbar:bottom:" + this.id),
				this.reg.getPref("actions.rolesmap.groupOrder", "refresh users *"), this),
			uiContext: "bar",
			actionContext: this,
			disableFullOverlay: true,
		} as OBarActionsInit<JRolesMapActionCtx>));
		this._showToolbarBottom.setAttribute("class", "showToolbarBottom");

		// Form details
		this._editUserRolesToolbar = this._detailsBoxElt.appendChild(new BarActions<ObjRolesMapEdit<OBJCTX, E>>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:rolesmap:edit:toolbar", "actions:rolesmap:edit:toolbar:" + this.id),
				this.reg.getPref("actions.rolesmap.groupOrder", "edit *"), this),
			uiContext: "bar",
			actionContext: this,
			disableFullOverlay: true,
		} as OBarActionsInit<ObjRolesMapEdit<OBJCTX, E>>));
		this._editUserRolesToolbar.setAttribute("class", "detailToolbar");
		//this._editUserRolesToolbar.setAttribute("hidden", "true");

		this.formEditElt = this._detailsBoxElt.appendChild(<form id="form" autocomplete="off"/>) as HTMLFormElement;
		//await this.initFormUi();

		let onFormEditRequest = 0;
		this.formEditElt.addEventListener('input', () => {
			if (onFormEditRequest) {
				clearTimeout(onFormEditRequest);
				onFormEditRequest = 0;
			}
			onFormEditRequest = setTimeout(async () => {
				await this.computeDirtyStatus();
				onFormEditRequest = 0;
			}, 300) as any;
		});
		this.formEditElt.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if (ev.key === 's' && ACTION.isAccelPressed(ev)) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				UserFormSaveAction.SINGLETON.executeIfAvailable(this, ev);
				return true;
			}
			return false;
		});

		this._dirtyMdgOver.addEventListener("dblclick", (ev) => {UserFormRevertAction.SINGLETON.executeIfAvailable(this, ev)});

		this.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if (ev.key === 'Enter' && ACTION.isAccelPressed(ev)) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				if (init.uiContext === "popup" && await VIEWS.canHideView(this)) {
					POPUP.findPopupableParent(this).close(false);
				}
				return true;
			}
			return false;
		});

		return this.refreshUi(this.params?.preSelectAccounts?.map(entry => {return {account: entry}}));
	}

	/** Refresh UI */
	async _refresh() {
		this.refreshFreeze(1);
		try {
			super._refresh();
			await this.refreshUi();
		} finally {
			this.refreshFreeze(-1);
		}
	}

	async refreshUi(selectRolesMapEntries?: JUserSetRoles[]) {
		await this.fetchRolesMap(selectRolesMapEntries);
	}

	/** Retourne true si le compte a déjàdes rôles affectés (et que le dateHolter est chargé) */
	isAccountInMap(account: string): boolean {
		return this._dataHolder.getDatas().find((entry) => entry.account === account) ? true : false;
	}

	/**
	 * Tree
	 */

	/** Affecte des modifications d'utilisateurs dans le tree
	 * 	NOTE : aucun enregistrement n'est réalisé via cette fonction
	 */

	/*
	userRolesChange(userRoles: JUserSetRoles | JUserSetRoles[], remove:boolean=false){
		if (userRoles){
			if(Array.isArray(userRoles))
				userRoles.forEach((entry)=>this.userRolesChange(entry, remove));
			else{
				let datas = this._dataHolder.getDatas();
				let userEntry = datas.find((entry) => entry.account === userRoles.account);
				if (userEntry) {
					const offset = this._dataHolder.getOffset(userEntry);
					if(remove){
						this._dataHolder.deleteRowKey(this._dataHolder.getRowKey(offset));
					}else{
						Object.keys(userEntry).forEach((key) => {(userEntry as any)[key] = undefined});
						Object.assign(userEntry, userRoles);
						this._rolesGridElt.invalidateRows(offset, 1);
					}
				}
			}
		}
	}
	*/

	/** Rafraichissement global du tree
	 * 	NOTE : aucun enregistrement n'est réalisé via cette fonction
	 */
	private allUserRolesChange(usersRolesMap: JUserRolesMap): void {
		let usersRoles: JUserSetRoles[] = [];
		Object.keys(usersRolesMap).forEach((account) => {
			usersRoles.push({
				account,
				grantedRoles: usersRolesMap[account].allowedRoles,
				refusedRoles: usersRolesMap[account].deniedRoles,
				inheritedRoles: usersRolesMap[account].inheritedRoles,
				userUnknown: usersRolesMap[account].userUnknown
			})
		});
		this._dataHolder.setDatas(usersRoles);
	}

	/** Raffraichit le tree avec préservation de la sélection */
	async fetchRolesMap(selectRolesMapEntries?: JUserSetRoles[]) {
		try {
			this._lastLoadErrorStatus = undefined;
			const selectedEntries = selectRolesMapEntries ? selectRolesMapEntries : ((this._dataHolder && this._dataHolder.countRows() > 0) ? this._dataHolder.getSelectedDatas() as JUserSetRoles[] : []);
			this.allUserRolesChange(await this.params.objFetchRolesMap(this.params.objThis));
			this._lastLoadErrorStatus = false;
			if (selectedEntries) this.selectRolesMapEntry(selectedEntries);
		} catch (e) {
			this._lastLoadErrorStatus = true;
			this.allUserRolesChange({});
			await ERROR.show("Accès à la liste des rôles en échec", e);
		}
	}

	private _lastLoadErrorStatus: boolean;

	/**
	 * Enregistre un usersSetRoles et raffraichit les entrées du tree correspondant.
	 *
	 */
	async saveRolesMap(usersSetRoles: JUserSetRoles[]): Promise<JUserRolesMap | null> {
		let usersRolesMap: JUserSpecifiedRolesMap = {};
		let isEmpty = true;
		usersSetRoles.forEach((entry) => {
			if (entry.account) {
				usersRolesMap[entry.account] = (entry.grantedRoles || entry.refusedRoles) ? {allowedRoles: entry.grantedRoles, deniedRoles: entry.refusedRoles} : null;
				isEmpty = false;
			}
		});
		if (!isEmpty) {
			let resp = await this.params.objEditRolesMap(this.params.objThis, usersRolesMap);
			this.showDetails(null);
			if (resp != null) this.allUserRolesChange(resp);
			else this.fetchRolesMap()
			return resp;
		}
	}

	/**
	 * Sélection des entrées de l'arbre via une liste de JUser sur un tree **chargé**
	 * Si un itemType demandé n'est pas présent dans le tree, aucune erreur n'est remontée
	 * @param entries
	 */
	selectRolesMapEntry(entries: JUserSetRoles[]): void {
		if (entries?.length) {
			const selectedRows: number[] = [];
			for (let i = 0, c = this._dataHolder.countRows(); i < c; i++) {
				const userRow = this._dataHolder.getRow(i).rowDatas as JUser;
				if (userRow && entries.find(entry => entry && entry.account == userRow.account))
					selectedRows.push(i);
			}
			this._rolesGridElt.setSelectedRows(selectedRows);
		}
	}


	/**
	 * Initialisation des fieldset du formulaire
	 * Retourne true si le user a été affiché
	 */
	async showDetails(userRoles: JUserSetRoles[] | null, forced: boolean = false): Promise<boolean> {
		if (!forced && (userRoles === null || userRoles.length == 0)) {
			DOM.setHidden(this._detailsBoxElt, true);
			this._detailUserCurrentData = undefined;
			this.dirtyStatus = false;
			this._editUserRolesToolbar.refreshContent();
			return false;
		} else if (userRoles && userRoles.length == 1) {
			DOM.setHidden(this._detailsBoxElt, false);
			await this.initFormUi('single', true);
			FORMS.jsonToForm(userRoles[0], this.formEditElt, false, true);
			this._detailUserCurrentData = FORMS.formToJson(this.formEditElt);
			this.formEditElt.checkValidity();
			this.formEditElt.querySelectorAll("fieldset").forEach((fieldsetElt: HTMLFieldSetElement) => {
				fieldsetElt.disabled = this.isReadOnlyForm(userRoles);
			});
			this.dirtyStatus = false;
			this._editUserRolesToolbar.refreshContent();
			return true;
		} else if (userRoles && userRoles.length > 1) {
			// sélection multiple de users
			DOM.setHidden(this._detailsBoxElt, false);
			await this.initFormUi('multi', true);
			FORMS.jsonToForm([], this.formEditElt, false, true);
			this._detailUserCurrentData = FORMS.formToJson(this.formEditElt);
			this.formEditElt.checkValidity();
			this.formEditElt.querySelectorAll("fieldset").forEach((fieldsetElt: HTMLFieldSetElement) => {
				fieldsetElt.disabled = this.isReadOnlyForm(userRoles);
			});
			this.dirtyStatus = false;
			this._editUserRolesToolbar.refreshContent();
			return true;
		} else {
			// forced
			DOM.setHidden(this._detailsBoxElt, false);
			await this.initFormUi('new', true);
			await FORMS.jsonToForm([], this.formEditElt, false, true);
			this._detailUserCurrentData = FORMS.formToJson(this.formEditElt);
			this.formEditElt.checkValidity();
			this.formEditElt.querySelectorAll("fieldset").forEach((fieldsetElt: HTMLFieldSetElement) => {
				fieldsetElt.disabled = this.isReadOnlyForm(userRoles);
			});
			this.dirtyStatus = "forcedTrue";
			this._editUserRolesToolbar.refreshContent();
			return true;
		}
	}

	/** Retourne true si le formulaire est readonly */
	protected isReadOnlyForm(user: JUserSetRoles | JUserSetRoles[] | null): boolean {
		if (!this.params.objEditRolesMapPerms || !this.reg.hasPerm(this.params.objEditRolesMapPerms)) return true;
		return false;
	}

	/** Reset/Initialisation du formulaire (def du/des fieldset)
	 * @param onlyIfNecessay : recalcul de la zone qu'en cas de changement de contexte ui
	 * */
	private async initFormUi(morphology: 'single' | 'multi' | 'new' = 'single', onlyIfNecessay?: boolean): Promise<void> {
		// permet d'éviter un effet de flash sur changement de user
		if (onlyIfNecessay && this._lastFormMorphology === morphology)
			return;
		// Recalcul de l'area
		this.formEditElt.innerHTML = "";
		this.formEditElt.appendChild(
			<fieldset>
				<div class="fields" area-ids="*"/>
			</fieldset>);
		let listCodes = ["areas:rolesmap", "areas:rolesmap:" + morphology, "areas:rolesmap:" + morphology + ":" + this.id];
		await AREAS.applyLayout(this.formEditElt, await Promise.all(this.reg.mergeLists<IArea<JRolesMapActionCtx & IControlAreaContext> | Promise<IArea<JRolesMapActionCtx & IControlAreaContext>>>(...listCodes)), this);
		this._formEditEmptyData = FORMS.formToJson(this.formEditElt);
		this._lastFormMorphology = morphology;
	}

	private _lastFormMorphology: 'single' | 'multi' | 'new' | undefined;

	/** Retourne true si au moins une valeur du formulaire a changé */
	get dirtyStatus(): boolean | "forcedTrue" {return this._dirtyStatus}

	set dirtyStatus(val: boolean | "forcedTrue") {
		if (this._dirtyStatus != val) {
			this._dirtyStatus = val;
			//DOM.setHidden(this._editUserRolesToolbar, !this._dirtyStatus);
			this._editUserRolesToolbar.refreshContent();
			if (this.dirtyStatus === true || this.dirtyStatus === "forcedTrue") {
				this._dirtyMdgOver.showMsgOver(this._headBoxElt);
			} else {
				this._dirtyMdgOver.removeMsg();
			}
		}
	}

	private _dirtyStatus: boolean | "forcedTrue" = false;
	_dirtyMdgOver: MsgOver = <c-msg-over class="disableMsgOver"/> as MsgOver;

	async computeDirtyStatus(): Promise<boolean | "forcedTrue"> {
		if (this._detailsBoxElt.hasAttribute("hidden"))
			return false;
		if (this.dirtyStatus === "forcedTrue") return;
		const userData = FORMS.formToJson(this.formEditElt);
		/*console.log("DIRTY :: ", JSON.stringify(wspData) !== JSON.stringify(this._lastWspData));
		console.log("LAST :: ", JSON.stringify(this._lastWspData));
		console.log("NEW  :: ", JSON.stringify(wspData));*/
		this.dirtyStatus = JSON.stringify(userData) !== JSON.stringify(this._detailUserCurrentData);
		return this.dirtyStatus;
	}

	/**
	 * Tree
	 */
	redrawLine(row: GridDataRowJson<JUserSetRoles>, line: HTMLElement): void {
		if (row.rowDatas.userUnknown)
			line.title = "Utilisateur / groupe inconnu";
		else
			delete line.title;
		DOM.setAttrBool(line, "data-user-unknown", row.rowDatas.userUnknown);
	}

	/**
	 * IViewApi
	 */
	onViewBeforeHide(close?: boolean): boolean {
		return false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return !await this.computeDirtyStatus() || await POPUP.confirm(`Des propriétés n'ont pas été enregistrées. Voulez-vous abandonner ces modifications et quitter ?`, this.reg.env.uiRoot, {
			okLbl: "Abandonner les modifications et quitter",
			cancelLbl: "Reprendre l'édition"
		});
	}

}

customElements.define("rolesmap-edit", ObjRolesMapEdit);

REG.reg.registerSkin('rolesmap-edit', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		overflow: hidden;
	}

	#head {
		display: flex;
		flex: 1;
		overflow: auto;
	}

	c-grid-small {
		flex: 1;
	}

	#toolbarsBox {
		display: flex;
		flex-direction: column;
	}

	c-bar-actions.showToolbarTop {
		flex: 1;
	}

	c-bar-actions.showToolbarTop,
	c-bar-actions.showToolbarBottom {
		flex-direction: column;
		border-inline-start: 1px solid var(--border-color);
	}


	c-bar-actions.detailToolbar {
		flex-direction: row;
		border-bottom: 1px solid var(--border-color);
	}

	c-bar-actions.detailToolbar[hidden] {
		display: none;
	}

	c-resizer {
		pointer-events: auto;
		cursor: ns-resize;
		height: 2px;
	}

	.disableMsgOver {
		background-color: rgba(0, 0, 0, .2);
	}

	/* Form */
	#details {
		display: flex;
		flex-direction: column;
		overflow: auto;
	}

	#details[hidden] {
		display: none;
	}

	form {
		flex-direction: column;
		padding: .4em;
		overflow: auto;
	}

	fieldset {
		border: none;
		padding: 0;
		margin: 0;
		min-width: 0;
	}

	.lbl {
		min-width: 4em;
	}

	.fields > * {
		flex: 1;
		display: grid;
		min-width: 0;
		grid-template-columns: auto 1fr;
		grid-row-gap: .7rem;
		grid-auto-rows: max-content;
	}

	c-input-users-panel, c-input-roles-selector {
		flex: 1
	}

`);
REG.reg.registerSkin('rolesmap-edit-tree', 1, /* language=CSS */ `
	*[data-user-unknown] {
		font-style: italic;
		color: var(--warning-color);
	}

	.error {
		color: var(--error-color);
	}

	.icon {
		background-size: 1rem;
	}

	/** Mise en forme de CellBuilderUserRoles */
	.role-unknown {
		text-decoration: line-through;
	}

	.role-inherited {
		font-style: italic;
	}

`);

/** Areas **/
/** Sélection de l'utilisateur / groupe */
export class SelectUserInputArea<C extends JRolesMapActionCtx & IControlAreaContext> extends ControlAsyncArea<C> {
	constructor(id: string, public rolesMapEdit: ObjRolesMapEdit<any, any>) {
		super(id || "account");
		this.setLabel("Compte");
	}

	async _loadControl(ctx: C, name: string): Promise<InputUserPanel<any>> {
		let userSelector = new InputUserPanel().initialize({
			name: this._id,
			required: true,
			reg: ctx.reg,
			emptySelectionMsg: "Choisir un compte...",
			userCard: "multi",
			usersGridInit: {
				usersSrv: ctx.reg.env.universe.useUsers,
				grid: {selType: "multi"},
				filterType: null,
				filterTypeInputVisibility: true,
				normalizeList: this.rolesMapEdit.params.addAnonymousAccount ? (array: JUser[], filterTxtRegExp?: RegExp) => {
					if (!array.find(entry => entry.account === this.rolesMapEdit.params.addAnonymousAccount))
						array.push({account: this.rolesMapEdit.params.addAnonymousAccount, isAnonymous: true});
					return array;
				} : null,
				openFilter: (entry) => !this.rolesMapEdit.isAccountInMap(entry.account),
			},
		} as OInputUserInit<any>);
		await userSelector.initializedAsync;

		// maj de la zone "roles"
		userSelector.addEventListener("change", async (evt) => {
			if (userSelector.value && userSelector.value.length > 0) {
				const resp = await this.rolesMapEdit.params.objFetchRolesMapForAccounts(this.rolesMapEdit.params.objThis, userSelector.value);
				ctx.userRoles = [];
				for (const userAccount in resp) {
					if (resp[userAccount])
						ctx.userRoles.push({
							account: userAccount,
							grantedRoles: resp[userAccount].allowedRoles,
							refusedRoles: resp[userAccount].deniedRoles,
							inheritedRoles: resp[userAccount].inheritedRoles,
						});
				}
			} else
				ctx.userRoles = [];
			await (userSelector.form.querySelector("c-input-roles-selector") as RolesSelectorInput).setInheritedRoles((ctx.userRoles && ctx.userRoles.length == 1) ? ctx.userRoles[0].inheritedRoles : null);
		});
		return userSelector;
	}
}

/** Actions **/

abstract class RolesMapAction<E extends JRolesMapActionCtx = JRolesMapActionCtx> extends Action<E> {
	protected mode: 'mono' | 'multi' = null;

	setMode(mode: 'mono' | 'multi') {
		this.mode = mode;
	}

	isVisible(ctx: E): boolean {
		if (!ctx.reg) return false;
		if (this.mode === "mono" && (!ctx.userRoles || ctx.userRoles.length !== 1)) return false;
		if (this.mode === "multi" && (!ctx.userRoles || ctx.userRoles.length === 0)) return false;
		return super.isVisible(ctx);
	}
}

/** Action refresh UI */
const actionRefreshUi = new Action<any>("refreshUi")
	.setLabel("Rafraichir")
	.setGroup("refresh")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute(async function (this: Action<any>, ctx: any, ev: Event) {
			const me = DOMSH.findHost<ObjRolesMapEdit<any, any>>(ev.target as Node);
			await me.refresh();
		}
	);

/** Formulaire : reset du formulaire */
class UserFormRevertAction<E extends ObjRolesMapEdit<any, any>> extends RolesMapAction<E> {
	static SINGLETON = new UserFormRevertAction();

	constructor(id?: string) {
		super(id || "userFormRevert");
		this._label = "Annuler";
		this.setGroup("edit");
		this.setIcon("/@skin@/core/dialogs/cancel.svg");
	}

	isEnabled(ctx: E): boolean {
		if (!ctx.formEditElt || ctx.formEditElt.hasAttribute("disabled"))
			return false;
		if (!ctx.dirtyStatus)
			return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: ObjRolesMapEdit<any, any>, ev?: Event): Promise<boolean> {
		// si pas execute issu de "this", confirmation
		if (ctx.dirtyStatus === "forcedTrue" || ((ev.target as Element).id === this.getId() || await POPUP.confirm(`Voulez-vous abandonner les modifications en cours ?`, ctx.reg.env.uiRoot, {
			okLbl: "Abandonner les modifications",
			cancelLbl: "Reprendre l'édition"
		}))) {
			return ctx.showDetails(ctx.userRoles);
		}
	}
}


/** Formulaire : enregistrement des propriétés d'un utilisateur */
class UserFormSaveAction<E extends ObjRolesMapEdit<any, any>> extends RolesMapAction<E> {
	static SINGLETON = new UserFormSaveAction();

	constructor(id?: string) {
		super(id || "userFormSave");
		this._label = "Enregistrer";
		this.setGroup("edit");
		this.setIcon("/@skin@/core/dialogs/save.svg");
	}

	isEnabled(ctx: E): boolean {
		if (!ctx.formEditElt || ctx.formEditElt.hasAttribute("disabled"))
			return false;
		if (!ctx.dirtyStatus)
			return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: E, ev?: Event): Promise<void> {
		if (ctx.formEditElt.reportValidity()) {
			let msg = new MsgOver();
			await msg.setCustomMsg("Veuillez patienter...", "info").showMsgOver(ctx).waitFor(
				async () => {
					try {
						const params: JUserSetRoles = FORMS.formToJson(ctx.formEditElt);
						if (ctx.userRoles) {
							ctx.userRoles.forEach((entry) => {
								Object.assign(entry, {grantedRoles: params.grantedRoles, refusedRoles: params.refusedRoles} as JUserSetRoles);
							});
							// On filtre les users ajoutés, sans directive
							const filteredList = ctx.userRoles.filter((entry) => ctx.isAccountInMap(entry.account) || entry.grantedRoles?.length || entry.refusedRoles?.length);
							if (filteredList.length)
								await ctx.saveRolesMap(filteredList);
							else
								POPUP.showNotifWarning("Aucune information à enregistrer", ctx.formEditElt);
						}
					} catch (e) {
						ERROR.report(`L'enregistrement a échoué. Veuillez réessayer ultérieurement.`, e);
					}
				}
			);
		}
	}
}

/** Création d'une entrée */
export class CreateEntryAction<E extends ObjRolesMapEdit<any, any>> extends RolesMapAction<E> {
	static SINGLETON = new CreateEntryAction();

	constructor(id?: string) {
		super(id || "createEntry");
		this.setLabel("Modifier les permissions pour un utilisateur");
		this.setDescription("Modifier les permissions pour un utilisateur");
		this._group = "add";
		this.setIcon("/@skin@/commons/icons/new.svg");
	}

	async execute(ctx: E, ev?: Event): Promise<void> {
		let msg;
		await ctx.showDetails(null, true);
	}
}

/** Suppression d'une ou plusieurs entrées */
export class DeleteEntriesAction<E extends ObjRolesMapEdit<any, any>> extends RolesMapAction<E> {
	static SINGLETON = new DeleteEntriesAction();

	constructor(id?: string) {
		super(id);
		this.setMode("multi");
		this._label = (ctx) => {
			if (ctx.userRoles && ctx.userRoles.length > 1) return "Supprimer les déclarations de rôles"
			else return "Supprimer la déclaration de rôles";
		};
		this._description = (ctx) => {
			if (ctx.userRoles && ctx.userRoles.length > 1) return "Suppression des déclarations de rôles"
			else return "Suppression de la déclaration de rôles";
		};
		this._group = "remove";
		this.setIcon("/@skin@/commons/icons/delete.svg");
	}

	isEnabled(ctx: E): boolean {
		if (ctx.dirtyStatus !== false) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: E, ev?: Event): Promise<boolean> {
		let msg;
		if (ctx.userRoles.length === 1) {
			msg = `Voulez-vous vraiment supprimer cette déclaration de rôles ?`;
		} else {
			const usersCount = ctx.userRoles.length;
			msg = `Voulez-vous vraiment supprimer ces '${usersCount}' déclarations de rôles ?`;
		}

		if (await POPUP.confirm(msg, null, {okLbl: "Supprimer", cancelLbl: "Annuler"})) {
			try {
				if (ctx.userRoles) {
					ctx.userRoles.forEach((entry) => {
						delete entry.grantedRoles;
						delete entry.refusedRoles;
					});
					if (!await ctx.saveRolesMap(ctx.userRoles)) return false;
				}
				return true;
			} catch (e) {
				await ERROR.report(`La suppression des déclarations de rôles n'a pas pu aboutir. Veuillez réessayer ultérieurement.`, e);
			}
		}
		return false;
	}
}